import { db, type PhotoEntry, type PhotoStatus } from './localDb.js'
import { uuidv7 } from './ids.js'
import { backoffFor } from './outbox.js'
import { api } from './apiClient.js'
import { ApiError } from './errors.js'
import { toBase64 } from './images.js'

/**
 * Fotowarteschlange – Grundlage für Block 4.
 *
 * Getrennt vom Ausgangskorb, und zwar aus einem sehr konkreten Grund: ein
 * Rapport mit zehn Fotos wäre ein Eintrag von mehreren Megabyte. Bei
 * schlechtem Netz geht der nie durch, und der Monteur sieht seit einer Stunde
 * "wartet".
 *
 * Getrennt heisst: der Rapport ist ein kleiner JSON-Eintrag, geht sofort durch
 * und ist beim Chef sichtbar. Die Fotos folgen einzeln und hängen sich
 * nachträglich an. Angezeigt wird "Übertragen · 3 von 5 Fotos" statt nichts.
 *
 * Block 4 ergänzt hier: Verkleinern auf 1600 px, EXIF-Drehung berücksichtigen,
 * signierte Upload-Adressen, Fortsetzung nach Abbruch.
 */

/** Längste Kante nach dem Verkleinern. Für Dokumentationszwecke ausreichend. */
export const MAX_EDGE_PX = 1600
export const JPEG_QUALITY = 0.8

export interface AddPhotoInput {
  tenantId: string
  ownerType: 'rapport' | 'quoterequest'
  ownerId: string
  blob: Blob
  width?: number
  height?: number
}

export async function addPhoto(input: AddPhotoInput): Promise<PhotoEntry> {
  const entry: PhotoEntry = {
    id: uuidv7(),
    createdAt: Date.now(),
    tenantId: input.tenantId,
    ownerType: input.ownerType,
    ownerId: input.ownerId,
    blob: input.blob,
    bytes: input.blob.size,
    width: input.width ?? null,
    height: input.height ?? null,
    status: 'local',
    attempts: 0,
    nextAttemptAt: 0,
    storageKey: null,
    uploadUrl: null,
    lastError: null
  }
  await db.photos.add(entry)
  return entry
}

export interface PhotoProgress {
  total: number
  done: number
  failed: number
  pending: number
}

/** Für die Anzeige am Rapport: "Übertragen · 3 von 5 Fotos". */
export async function progressFor(ownerId: string): Promise<PhotoProgress> {
  const all = await db.photos.where('ownerId').equals(ownerId).toArray()
  const done = all.filter((p) => p.status === 'confirmed' || p.status === 'uploaded').length
  const failed = all.filter((p) => p.status === 'failed').length
  return { total: all.length, done, failed, pending: all.length - done - failed }
}

export async function photosFor(ownerId: string): Promise<PhotoEntry[]> {
  return db.photos.where('ownerId').equals(ownerId).toArray()
}

export async function setStatus(
  id: string,
  status: PhotoStatus,
  patch: Partial<PhotoEntry> = {}
): Promise<void> {
  await db.photos.update(id, { status, ...patch })
}

export async function markFailed(id: string, message: string): Promise<void> {
  const entry = await db.photos.get(id)
  if (!entry) return
  await db.photos.update(id, {
    status: 'failed' as PhotoStatus,
    attempts: entry.attempts + 1,
    nextAttemptAt: Date.now() + backoffFor(entry.attempts),
    lastError: message
  })
}

/**
 * Nach bestätigtem Upload darf der lokale Blob weg – aber erst dann.
 * Vorher gilt: nie löschen, bevor der Server bestätigt hat.
 */
export async function releaseBlob(id: string): Promise<void> {
  const entry = await db.photos.get(id)
  if (!entry || entry.status !== 'confirmed') return
  await db.photos.update(id, { blob: null })
}

export async function pendingCount(): Promise<number> {
  return db.photos.where('status').anyOf('local', 'presigning', 'uploading').count()
}

let laeuft = false

/**
 * Fotos übertragen – eines nach dem anderen.
 *
 * Eines nach dem anderen ist hier kein Ordnungsfimmel: bei einer Verbindung,
 * die für ein Bild kaum reicht, machen fünf gleichzeitige Uploads alle fünf
 * langsamer und keinen fertig. Nacheinander ist nach dem ersten Bild eines
 * durch, und die Anzeige springt von "0 von 5" auf "1 von 5".
 *
 * Ein Foto, dessen Rapport noch gar nicht übertragen ist, wird übersprungen
 * statt abgewiesen – der Ausgangskorb ist gleich damit durch, und beim
 * nächsten Durchlauf klappt es.
 */
export async function drainPhotos(options: { online?: boolean } = {}): Promise<{
  uploaded: number
  failed: number
  skipped: number
}> {
  const ergebnis = { uploaded: 0, failed: 0, skipped: 0 }
  if (laeuft || options.online === false) return ergebnis
  laeuft = true

  try {
    for (;;) {
      const jetzt = Date.now()
      const kandidaten = await db.photos
        .where('status')
        .anyOf('local', 'failed', 'uploading')
        .toArray()
      const naechstes = kandidaten
        .filter((p) => p.nextAttemptAt <= jetzt && p.blob)
        .sort((a, b) => a.id.localeCompare(b.id))[0]
      if (!naechstes) break

      await setStatus(naechstes.id, 'uploading')
      try {
        const base64 = await toBase64(naechstes.blob as Blob)
        await api.uploadPhoto(naechstes.ownerId, {
          clientPhotoId: naechstes.id,
          image: base64,
          width: naechstes.width ?? undefined,
          height: naechstes.height ?? undefined
        })
        await setStatus(naechstes.id, 'confirmed', { lastError: null })
        // Erst jetzt darf der lokale Blob weg – nicht eine Zeile früher.
        await releaseBlob(naechstes.id)
        ergebnis.uploaded++
      } catch (err) {
        const fehler = err instanceof ApiError ? err : null
        // Der Rapport ist noch nicht beim Server. Kein Fehler, nur zu früh.
        if (fehler?.status === 404) {
          await setStatus(naechstes.id, 'local', { nextAttemptAt: Date.now() + 3_000 })
          ergebnis.skipped++
          break
        }
        if (fehler && !fehler.retryable && fehler.kind !== 'network') {
          await db.photos.update(naechstes.id, {
            status: 'failed' as PhotoStatus,
            attempts: naechstes.attempts + 1,
            // Ein dauerhaft abgewiesenes Foto nicht endlos wiederholen.
            nextAttemptAt: Number.MAX_SAFE_INTEGER,
            lastError: fehler.message
          })
          ergebnis.failed++
          continue
        }
        await markFailed(naechstes.id, fehler?.message ?? 'Übertragung fehlgeschlagen.')
        ergebnis.failed++
        break // bei Netzproblemen nicht die ganze Warteschlange durchprobieren
      }
    }
    return ergebnis
  } finally {
    laeuft = false
  }
}

/** Beim Start: ein mitten im Upload unterbrochenes Foto wieder aufnehmen. */
export async function recoverInterruptedPhotos(): Promise<number> {
  const offen = await db.photos.where('status').equals('uploading').toArray()
  for (const p of offen) {
    await db.photos.update(p.id, { status: 'local' as PhotoStatus, nextAttemptAt: 0 })
  }
  return offen.length
}
